<template>
  <section class="profile">
    <div class="profile__title">
      <h1 v-html="$t('profile.title')" />
    </div>
    <div class="profile__main">
      <section>
        <user-profile-info
          v-if="userDetails"
          :form="userDetails.type === 1 ? lists : listPro"
          :errors="$v.form"
          :is-editable="isEditable"
          :is-horizontal="true"
          @toggleEditor="toggleEditor"
          @updateInputs="updatedInputs"
          @updateDateInputs="updatedDateInputs"
          @updateSelecterInputs="updatedSelecter"
          @updateRadioButton="updatedRadioButton"
        />
        <complementary-info
          :minified="!isEditable"
          :optin-email="form.optin_email"
          :optin-sms="form.optin_sms"
          :errors="$v.form"
          @toggleMinified="toggleEditor"
          @submitForm="submitForm"
          @updatedRadioButton="updatedRadioButton"
        />
        <div
          v-if="!isFormSubmitting"
          class="profile__main__callback"
        >
          <p
            v-if="isSuccess"
            class="profile__main__callback__success"
          >
            {{ $t('profile.info.callBack.success') }}
          </p>
          <p
            v-if="isError"
            class="profile__main__callback__error"
          >
            {{ $t('profile.info.callBack.error') }}
          </p>
        </div>
        <div
          v-if="isEditable"
          class="profile__main__button"
        >
          <rent-button
            :loading="isFormSubmitting"
            @buttonclicked="submitForm()"
          >
            {{ $t('profile.info.submit') }}
          </rent-button>
          <confidentiality />
        </div>
      </section>
      <section>
        <my-rent-info />
        <user-previous-rental />
      </section>
    </div>
    <remove-account />
  </section>
</template>

<script>
import { mapState } from 'vuex'
import {
  required,
  email,
  numeric,
  minLength,
  maxLength,
  sameAs,
} from 'vuelidate/lib/validators'
import RentButton from '../shared/RentButton'
import individualList from '../../../data/forms/sign-up/profile.json'
import ProList from '../../../data/forms/sign-up/profilePro.json'
import datesInputsValidation from '../../../data/forms/datesInputsValidation'

export default {
  name: 'UserProfile',
  components: {
    RentButton,
    UserProfileInfo: () => import(/* webpackChunkName: "profile"  */ `./UserProfileInfo`),
    ComplementaryInfo: () => import(/* webpackChunkName: "profile"  */ `../shared/inputs/ComplementaryInfo/ComplementaryInfo`),
    Confidentiality: () => import(/* webpackChunkName: "profile"  */ `../shared/inputs/ComplementaryInfo/Confidentiality`),
    MyRentInfo: () => import(/* webpackChunkName: "profile"  */ `./MyRentInfo`),
    UserPreviousRental: () => import(/* webpackChunkName: "profile"  */ `./PreviousRental/UserPreviousRental`),
    RemoveAccount: () => import(/* webpackChunkName: "profile"  */ `./RemoveAccount`),
  },
  data() {
    return {
      lists: individualList,
      listPro: ProList,
      isLoading: false,
      form: {
        light: false,
        type: 1,
        company_siret: null,
        company_name: null,
        email: null,
        password: null,
        confirmation: null,
        civility: null,
        first_name: null,
        last_name: null,
        birthday: {
          day: null,
          month: null,
          year: null,
          custom: null,
        },
        birthplace: null,
        address: null,
        city: null,
        postal_code: null,
        country: null,
        tel: null,
        licence_number: null,
        licence_date: {
          day: null,
          month: null,
          year: null,
          custom: null,
        },
        licence_place: null,
        optin_email: null,
        optin_sms: null,
      },
    }
  },
  validations: {
    form: {
      email: { required, email },
      password: {
        minLength: minLength(2),
      },
      confirmation: {
        sameAsPassword: sameAs('password'),
      },
      company_name: {},
      company_siret: { numeric, minLength: minLength(14), maxLength: maxLength(14) },
      civility: { required },
      first_name: {
        required,
        minLength: minLength(2),
      },
      last_name: { required, minLength: minLength(2) },
      birthday: {
        ...datesInputsValidation,
      },
      birthplace: { required, minLength: minLength(2) },
      address: { required, minLength: minLength(2), maxLength: maxLength(40) },
      city: { required, minLength: minLength(2) },
      postal_code: {
        required, numeric, minLength: minLength(5), maxLength: maxLength(5),
      },
      tel: {
        required, numeric, minLength: minLength(10), maxLength: maxLength(10),
      },
      country: { required },
      type: { required },
      licence_number: { required, minLength: minLength(2) },
      licence_date: {
        ...datesInputsValidation,
      },
      licence_place: { required, minLength: minLength(2) },
      optin_email: { required },
      optin_sms: { required },
    },
  },
  computed: {
    ...mapState({
      userDetails: (state) => state.user.details,
      isFormSubmitting: (state) => state.user.submission.isFormSubmitting,
      isSuccess: (state) => state.user.submission.isSuccess,
      isError: (state) => state.user.submission.isError,
      isEditable: (state) => state.user.submission.isEditable,
      isGetProfileRequested: (state) => state.user.isGetProfileRequested,
      isLightUser: (state) => state.user.isLightUser,
    }),

  },
  mounted() {
    this.fillList()
    const isUserLight = window.localStorage.isLightUser
    if (isUserLight) {
      this.$store.commit('user/SET_FORM_TOGGLE_FORM_EDITABLE')
    }
  },
  watch: {
    userDetails: {
      handler() {
        this.fillList()
      },
      deep: true,
    },
    isGetProfileRequested: {
      handler(val) {
        if (!val || this.userDetails == null) {
          window.location = '/'
        }
      },
    },
  },

  methods: {
    fillList() {
      // we don't need password in the edit version, so remove the last item which is password
      // wait that the list is set
      if (!this.userDetails) return
      if (this.userDetails.type === 1) {
        this.lists.forms.list.forEach((entry, index) => {
          if (!this.userDetails[entry.name]) {
            return
          }
          if (entry.components === 'dateInputs') {
            return this.setDateInputs(entry, index)
          }

          if (entry.components === 'list') {
            return this.setList(entry, index)
          }

          this.lists.forms.list[index].value = this.userDetails[
            entry.name
          ]

          this.form[entry.name] = this.lists.forms.list[index].value
          // maybe this.$set(array, index, update) should be better
        })
      } else {
        this.listPro.forms.list.forEach((entry, index) => {
          if (!this.userDetails[entry.name]) {
            return
          }
          if (entry.components === 'dateInputs') {
            return this.setDateInputs(entry, index)
          }

          if (entry.components === 'list') {
            return this.setList(entry, index)
          }

          this.listPro.forms.list[index].value = this.userDetails[
            entry.name
          ]

          this.form[entry.name] = this.listPro.forms.list[index].value
          // maybe this.$set(array, index, update) should be better
        })
      }

      // Complementary information is outside FormBuilder (profile.json)
      this.form.optin_email = this.userDetails.optin_email
      this.form.optin_sms = this.userDetails.optin_sms
    },
    setList(entry, index) {
      if (this.userDetails.type === 1) {
        this.lists.forms.list[index].selected = this.userDetails[entry.name]
        this.form[entry.name] = this.lists.forms.list[index].selected
      } else {
        this.listPro.forms.list[index].selected = this.userDetails[entry.name]
        this.form[entry.name] = this.listPro.forms.list[index].selected
      }
    },
    setDateInputs(entry, index) {
      const [extractedYear, extractedMonth, extractedDay] = this.userDetails[entry.name].split('-')
      if (this.userDetails.type === 1) {
        this.lists.forms.list[index].value.day = extractedDay
        this.form[entry.name].day = extractedDay
        this.lists.forms.list[index].value.month = extractedMonth
        this.form[entry.name].month = extractedMonth
        this.lists.forms.list[index].value.year = extractedYear
        this.form[entry.name].year = extractedYear
      } else {
        this.listPro.forms.list[index].value.day = extractedDay
        this.form[entry.name].day = extractedDay
        this.listPro.forms.list[index].value.month = extractedMonth
        this.form[entry.name].month = extractedMonth
        this.listPro.forms.list[index].value.year = extractedYear
        this.form[entry.name].year = extractedYear
      }
      // set this value DB ready
      this.form[entry.name].custom = this.userDetails[entry.name]
    },
    toggleEditor() {
      this.$store.commit('user/SET_FORM_TOGGLE_FORM_EDITABLE')
    },
    updatedInputs(val) {
      this.form[val.name] = val.value
      if (this.$v.form[val.name]) {
        this.$v.form[val.name].$touch()
      }
    },
    updatedDateInputs(val) {
      this.form[val.name][val.category] = val.value
      if (this.$v.form[val.name] && this.$v.form[val.name][val.category]) {
        this.$v.form[val.name][val.category].$touch()
      }
    },
    updatedSelecter(val) {
      this.form[val.name] = val.value
      if (this.$v.form[val.name]) {
        this.$v.form[val.name].$touch()
      }
    },
    updatedRadioButton(radio) {
      this.form[radio.name] = radio.value
    },
    submitForm() {
      this.$v.form.$touch()
      if (this.$v.form.$invalid) {
        return
      }
      this.$store.dispatch('user/PUT_USER_INFO', this.form)
      const isUserLight = window.localStorage.isLightUser
      if (isUserLight) {
        localStorage.removeItem('isLightUser');
        window.location = '/devis/options'
      }
    },
  },
}
</script>

<style scoped lang="scss">

.profile {
  align-content: center;
  align-items: center;
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  padding: 0 30px;

  @include teq-breakpoint($wide) {
    padding: 0;
  }

  &__title {
    align-content: flex-start;
    display: flex;
    flex: 1;
    flex-direction: column;
    width: 100%;

    @include teq-breakpoint($wide) {
      max-width: calc(100% - 186px);
    }
  }

  &__main {
    align-content: flex-start;
    display: flex;
    flex: 1;
    flex-direction: column;
    width: 100%;

    @include teq-breakpoint($medium) {
      flex-direction: row;
      max-width: calc(100% - 186px);
    }

    > section {
      align-content: flex-start;
      display: flex;
      flex: 1;
      flex-direction: column;
      margin-bottom: 20px;

      @include teq-breakpoint($medium) {
        &:nth-child(odd) {
          margin-right: 10px;
        }

        &:nth-child(even) {
          margin-left: 10px;
        }
      }
    }

    @include teq-breakpoint($wide) {
      flex-direction: row;
    }

    &__button {
      padding: 30px;
    }

    &__callback {
      display: flex;

      &__success {
        color: $c-eucalyptus;
      }

      &__error {
        color: $c-error;
      }
    }
  }
}
</style>
